import { memo } from 'react';
import { Alert, Box, SpaceBetween, TextContent } from '@amzn/awsui-components-core';
import { ContentType } from '@amzn/awscat-pattern-library-service-model';

import ConfirmationModalView from '../v2-common/ConfirmationModalView';
import { MetadataTabAudienceView } from './MetadataTabAudienceView';
import { MetadataTabConfigurationView } from './MetadataTabConfigurationView';
import { MetadataTabDescriptionView } from './MetadataTabDescriptionView';
import { contentTypeDisplayNames } from '../utils/DisplayNames';
import { useMetadataTabViewModel } from './MetadataTabViewModel';

/** Modal body shown when the post-conversion save failed. The user's only escape is to retry — no Cancel, no reload. */
function ContentTypeSaveFailedExplanation() {
  return (
    <SpaceBetween size="s">
      <Box variant="p">
        Your content type was converted, but saving the result to the server failed. The converted
        content is held in this browser tab only.
      </Box>
      <Alert type="error" header="Don't reload this page">
        Reloading or closing this tab will discard the converted content and you'll need to start
        the conversion over. Use <strong>Retry save</strong> to send it to the server.
      </Alert>
    </SpaceBetween>
  );
}

interface ContentTypeChangeExplanationProps {
  readonly fromType: ContentType;
  readonly toType: ContentType;
}

/**
 * Modal body for the content-type confirmation dialog. Renders a
 * direction-specific explanation of how the conversion reshapes the
 * document so the author knows what to expect before confirming.
 */
function ContentTypeChangeExplanation({ fromType, toType }: ContentTypeChangeExplanationProps) {
  const toLabel = contentTypeDisplayNames.get(toType) ?? toType;

  if (fromType === ContentType.Pattern && toType === ContentType.Guide) {
    return (
      <SpaceBetween size="s">
        <Box variant="p">
          You're changing this <strong>Pattern</strong> into a <strong>Guide</strong>. Here's how
          the conversion works:
        </Box>
        <TextContent>
          <ul>
            <li>
              <strong>Summary</strong> stays where it is, on the Guide's home page.
            </li>
            <li>
              Every other Pattern section (Architecture, Tools, Best practices, References, and so
              on) becomes its own page in the Guide. You can rename or reorder these pages later.
            </li>
            <li>
              <strong>Epics</strong> become a single page. Each epic turns into a heading on that
              page, and each task under it becomes a sub-heading with its description.
            </li>
            <li>
              <strong>Troubleshooting</strong> becomes a page that lists each issue followed by its
              solution.
            </li>
          </ul>
        </TextContent>
        <Alert type="warning" header="This action cannot be undone">
          <SpaceBetween size="xs">
            <Box variant="p">
              The conversion runs and saves immediately when you confirm. You won't be able to
              revert to the Pattern version.
            </Box>
            <Box variant="p">
              <strong>All inline comments will be lost.</strong> Comment re-anchoring is currently
              broken, so any comments attached to this document will not carry over to the Guide.
            </Box>
          </SpaceBetween>
        </Alert>
      </SpaceBetween>
    );
  }

  if (fromType === ContentType.Guide && toType === ContentType.Pattern) {
    return (
      <SpaceBetween size="s">
        <Box variant="p">
          You're changing this <strong>Guide</strong> into a <strong>Pattern</strong>. Here's how
          the conversion works:
        </Box>
        <TextContent>
          <ul>
            <li>
              The Guide's home page (and any sections on it) move into the Pattern's{' '}
              <strong>Summary</strong> section. Each home-page section becomes a sub-heading inside
              Summary.
            </li>
            <li>
              Every other Guide page lands in the Pattern's <strong>Additional information</strong>{' '}
              section, one after another. The page title becomes a heading there. You can move that
              content into other Pattern sections (Architecture, Tools, etc.) after the conversion.
            </li>
            <li>
              The Pattern's other sections (Prerequisites, Architecture, Tools, Epics,
              Troubleshooting, References) start out empty. You'll need to fill them in yourself —
              the converter doesn't try to guess where each piece of Guide content belongs.
            </li>
          </ul>
        </TextContent>
        <Alert type="warning" header="This action cannot be undone">
          <SpaceBetween size="xs">
            <Box variant="p">
              The conversion runs and saves immediately when you confirm. You won't be able to
              revert to the Guide version.
            </Box>
            <Box variant="p">
              <strong>All inline comments will be lost.</strong> Comment re-anchoring is currently
              broken, so any comments attached to this document will not carry over to the Pattern.
            </Box>
          </SpaceBetween>
        </Alert>
      </SpaceBetween>
    );
  }

  return (
    <Box variant="p">
      Changing content type to <strong>{toLabel}</strong> isn't supported in the new editor yet. Use
      the legacy editor for this change.
    </Box>
  );
}

/** Top-level view for the Metadata tab. Composes the three section views and renders the content-type confirmation modal off VM state. */
export const MetadataTabView = memo(function MetadataTabView() {
  const vm = useMetadataTabViewModel();

  if (!vm) return null;

  const isModalOpen = vm.pendingContentTypeChange !== undefined || vm.isContentTypeSavePending;

  return (
    <>
      <SpaceBetween size="l">
        <MetadataTabDescriptionView vm={vm} />
        <MetadataTabConfigurationView vm={vm} onContentTypeRequest={vm.requestContentTypeChange} />
        <MetadataTabAudienceView vm={vm} />
      </SpaceBetween>
      {isModalOpen && (
        <ConfirmationModalView
          header={
            vm.isContentTypeSavePending
              ? 'Saving the content type change failed'
              : 'Change content type'
          }
          body={
            vm.isContentTypeSavePending ? (
              <ContentTypeSaveFailedExplanation />
            ) : (
              <ContentTypeChangeExplanation
                fromType={vm.contentTypeChangeSourceType ?? vm.contentType}
                toType={vm.pendingContentTypeChange!}
              />
            )
          }
          confirmLabel={vm.isContentTypeSavePending ? 'Retry save' : 'Change content type'}
          isConfirming={vm.isContentTypeChangeInFlight}
          isDismissible={!vm.isContentTypeSavePending}
          onDismiss={vm.cancelContentTypeChange}
          onConfirm={
            vm.isContentTypeSavePending
              ? () => void vm.retryContentTypeSave()
              : () => void vm.confirmContentTypeChange()
          }
        />
      )}
    </>
  );
});
